import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { C } from './LandingConstants';

export function AuthPanel({ isLogin, setIsLogin, form, setForm, handleSubmit, handleGoogleAuth, error, loading, showPassword, setShowPassword, onClose }) {
  const [showDisclaimer, setShowDisclaimer] = React.useState(false);
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: '24px 28px 28px', maxWidth: 400, width: '100%' }} className="animate-fade-in-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, color: C.text, fontWeight: 400, margin: 0 }}>
          {isLogin ? 'Welcome back' : 'Create your account'}
        </h2>
        {onClose && (
          <button data-testid="auth-panel-close" onClick={onClose}
            style={{ width: 30, height: 30, borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: C.cream, border: `1px solid ${C.border}`, color: C.textSec, cursor: 'pointer', fontSize: 16, lineHeight: 1, flexShrink: 0 }}>
            &times;
          </button>
        )}
      </div>

      <Button data-testid="google-auth-btn" onClick={handleGoogleAuth}
        className="w-full h-11 rounded-xl mb-3 font-medium"
        style={{ background: C.cream, border: `1px solid ${C.border}`, color: C.text }}>
        <svg viewBox="0 0 24 24" className="w-5 h-5 mr-3" fill="none">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </Button>

      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: `1px solid ${C.border}` }} /></div>
        <div className="relative flex justify-center"><span className="px-4 text-sm" style={{ color: C.muted, background: C.card }}>or</span></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {!isLogin && (
          <>
            <div>
              <Label className="text-sm font-medium" style={{ color: C.textSec }}>Name</Label>
              <Input data-testid="name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your name"
                className="mt-1.5 h-12 rounded-xl" style={{ background: C.cream, border: `1px solid ${C.border}`, color: C.text }} />
            </div>
            <div>
              <Label className="text-sm font-medium" style={{ color: C.textSec }}>Phone Number</Label>
              <Input data-testid="phone-input" type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 (555) 000-0000"
                className="mt-1.5 h-12 rounded-xl" style={{ background: C.cream, border: `1px solid ${C.border}`, color: C.text }} />
            </div>
          </>
        )}
        <div>
          <Label className="text-sm font-medium" style={{ color: C.textSec }}>Email</Label>
          <Input data-testid="email-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@example.com"
            className="mt-1.5 h-12 rounded-xl" style={{ background: C.cream, border: `1px solid ${C.border}`, color: C.text }} />
        </div>
        <div>
          <Label className="text-sm font-medium" style={{ color: C.textSec }}>Password</Label>
          <div className="relative">
            <Input data-testid="password-input" type={showPassword ? "text" : "password"} value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters"
              className="mt-1.5 h-12 rounded-xl pr-12" style={{ background: C.cream, border: `1px solid ${C.border}`, color: C.text }} />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5" style={{ color: C.muted }}>
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {error && <p data-testid="auth-error" className="text-sm" style={{ color: C.accent }}>{error}</p>}
        <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
          className="w-full h-12 rounded-xl font-semibold text-white" style={{ background: C.accent, border: 'none' }}>
          {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
        </Button>
        {!isLogin && (
          <div data-testid="signup-disclaimer" style={{
            padding: '8px 12px', borderRadius: 10,
            background: 'rgba(229, 168, 75, 0.06)', border: '1px solid rgba(229, 168, 75, 0.12)',
          }}>
            <p style={{ fontSize: 10, lineHeight: 1.5, margin: 0, color: C.muted, fontFamily: 'var(--font-sans)' }}>
              <strong style={{ color: C.textSec }}>Disclaimer:</strong> Nuri is not a substitute for professional medical advice.{' '}
              <button data-testid="show-full-disclaimer" onClick={(e) => { e.preventDefault(); setShowDisclaimer(!showDisclaimer); }}
                style={{ color: C.green, background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600, padding: 0, textDecoration: 'underline' }}>
                {showDisclaimer ? 'Show less' : 'Read full disclaimer'}
              </button>
            </p>
            {showDisclaimer && (
              <p style={{ fontSize: 10, lineHeight: 1.5, margin: '6px 0 0', color: C.muted, fontFamily: 'var(--font-sans)' }}>
                We are not medical doctors, registered dietitians, or licensed nutritionists. AI models can make mistakes — always verify nutritional information before making health decisions. Consult a qualified healthcare provider before starting any diet, exercise, or wellness program.
              </p>
            )}
          </div>
        )}
      </form>

      <p className="mt-4 text-center text-sm" style={{ color: C.muted }}>
        {isLogin ? "Don't have an account? " : "Already have an account? "}
        <button data-testid="toggle-auth-mode" onClick={() => setIsLogin(!isLogin)}
          className="font-medium hover:underline" style={{ color: C.green, background: 'none', border: 'none', cursor: 'pointer' }}>
          {isLogin ? 'Sign Up' : 'Sign In'}
        </button>
      </p>
    </div>
  );
}
